(() => {
  if (window.JuicedFullVideo) {
    window.JuicedFullVideo.init(document);
    return;
  }

  const instances = new Map();
  let hlsLibrary;

  function loadHlsLibrary(url) {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (!hlsLibrary) {
      hlsLibrary = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const timeout = setTimeout(() => fail(), 12000);
        const fail = () => {
          clearTimeout(timeout);
          script.remove();
          reject(new Error('Streaming player could not load'));
        };
        script.src = url;
        script.async = true;
        script.onload = () => {
          clearTimeout(timeout);
          if (window.Hls) resolve(window.Hls);
          else fail();
        };
        script.onerror = fail;
        document.head.append(script);
      }).catch((error) => {
        hlsLibrary = null;
        throw error;
      });
    }
    return hlsLibrary;
  }

  class FullVideoSection {
    constructor(section) {
      this.section = section;
      this.events = new AbortController();
      this.background = section.querySelector('[data-background-video]');
      this.toggle = section.querySelector('[data-background-toggle]');
      this.modal = section.querySelector('.video-modal');
      this.closeButton = section.querySelector('.video-modal-close');
      this.modalVideo = section.querySelector('.video-modal-video');
      this.modalIframe = section.querySelector('.video-modal-iframe');
      this.modalAttempt = 0;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.modalOpen = false;
      this.failed = false;
      this.ready = false;
      this.playPending = false;
      const bounds = section.getBoundingClientRect();
      this.inView = bounds.bottom > 0 && bounds.top < window.innerHeight;

      section.querySelectorAll('[data-video-modal]').forEach((button) => {
        this.listen(button, 'click', () => this.openModal(button));
      });
      this.listen(this.closeButton, 'click', () => this.closeModal());
      this.listen(this.modal, 'click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
      this.listen(document, 'keydown', (event) => this.onKeydown(event));
      this.listen(this.modalVideo, 'error', () => {
        if (this.modalVideo.dataset.playbackMode === 'hls-native') {
          this.playModalMp4(this.modalAttempt);
        }
      });

      if (!this.background) return;

      this.background.muted = true;
      this.background.defaultMuted = true;
      this.background.playsInline = true;
      this.listen(this.background, 'loadedmetadata', () => this.prepareLoop());
      ['loadeddata', 'canplay', 'seeked'].forEach((event) => {
        this.listen(this.background, event, () => this.syncPlayback());
      });
      this.listen(this.background, 'playing', () => {
        if (!this.shouldPlay()) {
          this.background.pause();
          return;
        }
        this.revealFrame();
        this.updateToggle();
        this.checkLoop();
      });
      this.listen(this.background, 'pause', () => {
        cancelAnimationFrame(this.frame);
        this.updateToggle();
      });
      this.listen(this.background, 'ended', () => {
        if (this.shouldPlay()) this.seekToStart();
      });
      this.listen(this.background, 'error', () => {
        this.failed = true;
        this.background.pause();
        this.section.classList.remove('has-background-frame');
        if (this.toggle) this.toggle.hidden = true;
      });
      this.listen(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.syncPlayback();
      });
      this.listen(document, 'visibilitychange', () => this.syncPlayback());
      this.listen(window, 'pageshow', () => this.syncPlayback());
      this.listen(this.motion, 'change', () => {
        this.userPaused = this.motion.matches;
        if (this.userPaused) this.section.classList.remove('has-background-frame');
        this.syncPlayback();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.syncPlayback();
        });
        this.observer.observe(section);
      }

      if (this.toggle) this.toggle.hidden = false;
      if (this.background.readyState >= 1) this.prepareLoop();
      this.syncPlayback();
    }

    listen(target, type, listener) {
      target?.addEventListener(type, listener, { signal: this.events.signal });
    }

    shouldPlay() {
      return !this.userPaused && !this.modalOpen && !this.failed && this.inView && !document.hidden;
    }

    prepareLoop() {
      const duration = this.background.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      const requestedStart = Number(this.section.dataset.loopStart) || 0;
      const requestedEnd = Number(this.section.dataset.loopEnd) || 0;
      this.start = Math.max(0, requestedStart);
      if (this.start >= duration - 0.1) this.start = 0;
      this.end = requestedEnd > this.start ? Math.min(requestedEnd, duration) : duration;
      if (this.end - this.start < 0.1) this.end = duration;
      this.background.loop = this.start === 0 && this.end === duration;
      this.ready = true;
      this.seekToStart();
    }

    seekToStart() {
      if (!this.ready) return;
      if (Math.abs(this.background.currentTime - this.start) > 0.025) {
        this.background.currentTime = this.start;
      }
      this.syncPlayback();
    }

    revealFrame() {
      if (this.shouldPlay() && this.ready && !this.background.seeking && this.background.readyState >= 2 &&
          this.background.currentTime >= this.start - 0.05 && this.background.currentTime < this.end) {
        this.section.classList.add('has-background-frame');
      }
    }

    syncPlayback() {
      if (!this.background || this.failed) return;
      this.updateToggle();
      if (!this.shouldPlay()) {
        this.background.pause();
        cancelAnimationFrame(this.frame);
        return;
      }
      this.background.preload = 'auto';
      if (this.ready && !this.background.seeking &&
          (this.background.currentTime >= this.end || this.background.currentTime < this.start - 0.1)) {
        this.background.currentTime = this.start;
      }
      if (this.playPending) return;
      if (!this.background.paused) {
        this.revealFrame();
        this.checkLoop();
        return;
      }
      this.playPending = true;
      this.background.play().then(() => {
        this.playPending = false;
        if (!this.shouldPlay()) this.background.pause();
        else if (this.background.paused) this.syncPlayback();
      }).catch((error) => {
        this.playPending = false;
        if (error.name === 'NotAllowedError') {
          this.userPaused = true;
          this.updateToggle();
        } else if (error.name === 'AbortError' && this.shouldPlay()) {
          this.syncPlayback();
        }
      });
    }

    checkLoop() {
      cancelAnimationFrame(this.frame);
      if (!this.ready || this.background.loop || !this.shouldPlay() || this.background.paused || this.background.seeking) return;
      if (this.background.currentTime >= this.end - 0.025) {
        this.seekToStart();
        return;
      }
      this.frame = requestAnimationFrame(() => this.checkLoop());
    }

    updateToggle() {
      if (!this.toggle) return;
      this.toggle.dataset.paused = String(this.userPaused);
      this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    }

    openModal(trigger) {
      if (!this.modal || this.modalOpen) return;
      instances.forEach((instance) => {
        if (instance !== this) instance.closeModal(false);
      });
      this.trigger = trigger;
      this.previousOverflow = document.body.style.overflow;
      this.modalOpen = true;
      this.modal.classList.add('visible');
      document.body.style.overflow = 'hidden';
      this.syncPlayback();
      if (this.modalIframe && this.section.dataset.youtubeId) {
        this.modalIframe.src = `https://www.youtube.com/embed/${encodeURIComponent(this.section.dataset.youtubeId)}?autoplay=1`;
      } else if (this.modalVideo) {
        this.startModalVideo(++this.modalAttempt);
      }
      this.closeButton?.focus();
    }

    closeModal(restoreFocus = true) {
      if (!this.modalOpen) return;
      this.modalOpen = false;
      this.modal.classList.remove('visible');
      document.body.style.overflow = this.previousOverflow;
      if (this.modalVideo) {
        this.modalAttempt += 1;
        this.modalHls?.destroy();
        this.modalHls = null;
        this.modalVideo.pause();
        this.modalVideo.currentTime = 0;
        this.modalVideo.removeAttribute('src');
        this.modalVideo.dataset.playbackMode = 'idle';
        this.modalVideo.load();
      }
      if (this.modalIframe) this.modalIframe.removeAttribute('src');
      if (restoreFocus) this.trigger?.focus();
      this.syncPlayback();
    }

    startModalVideo(attempt) {
      const video = this.modalVideo;
      const playlist = video.dataset.hlsSrc;
      if (!playlist) {
        this.playModalMp4(attempt);
        return;
      }
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.dataset.playbackMode = 'hls-native';
        video.src = playlist;
        video.play().catch(() => {});
        return;
      }
      if (!window.MediaSource && !window.ManagedMediaSource) {
        this.playModalMp4(attempt);
        return;
      }
      video.dataset.playbackMode = 'loading';
      loadHlsLibrary(video.dataset.hlsLibrarySrc).then((Hls) => {
        if (!this.modalOpen || attempt !== this.modalAttempt) return;
        if (!Hls.isSupported()) {
          this.playModalMp4(attempt);
          return;
        }
        const hls = new Hls({
          capLevelToPlayerSize: true,
          maxBufferLength: 20,
          maxMaxBufferLength: 30,
          maxBufferSize: 20000000,
          backBufferLength: 15,
          lowLatencyMode: false
        });
        this.modalHls = hls;
        video.dataset.playbackMode = 'hls';
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (this.modalOpen && attempt === this.modalAttempt && this.modalHls === hls) video.play().catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal && this.modalHls === hls) this.playModalMp4(attempt);
        });
        hls.loadSource(playlist);
        hls.attachMedia(video);
      }).catch(() => this.playModalMp4(attempt));
    }

    playModalMp4(attempt) {
      if (!this.modalOpen || attempt !== this.modalAttempt) return;
      const video = this.modalVideo;
      const time = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      this.modalHls?.destroy();
      this.modalHls = null;
      video.dataset.playbackMode = 'mp4';
      const desktop = video.querySelector('source[type="video/mp4"]')?.getAttribute('src');
      const source = window.matchMedia('(max-width: 767px)').matches ? video.dataset.mp4MobileSrc || desktop : desktop;
      if (!source) return;
      video.src = source;
      if (time > 0) {
        video.addEventListener('loadedmetadata', () => {
          if (this.modalOpen && attempt === this.modalAttempt && Number.isFinite(video.duration)) {
            video.currentTime = Math.min(time, Math.max(0, video.duration - 0.1));
          }
        }, { once: true, signal: this.events.signal });
      }
      video.play().catch(() => {});
    }

    onKeydown(event) {
      if (!this.modalOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
      } else if (event.key === 'Tab') {
        const focusable = [...this.modal.querySelectorAll('button, video[controls], iframe, [href], [tabindex="0"]')]
          .filter((element) => element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    destroy() {
      this.failed = true;
      this.closeModal(false);
      this.events.abort();
      this.observer?.disconnect();
      this.background?.pause();
      cancelAnimationFrame(this.frame);
    }
  }

  function init(root) {
    const sections = root.matches?.('.section-full-video') ? [root] : root.querySelectorAll('.section-full-video');
    sections.forEach((section) => {
      const previous = instances.get(section.id);
      if (previous?.section === section) return;
      previous?.destroy();
      instances.set(section.id, new FullVideoSection(section));
    });
  }

  window.JuicedFullVideo = { init };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, id) => {
      if (event.target.contains(instance.section)) {
        instance.destroy();
        instances.delete(id);
      }
    });
  });
})();
